'use client'

import Link from 'next/link'
import { useState } from 'react'
import { blogPostsData } from '@/data/blogPosts'
import { criticalBlogPosts } from '@/data/criticalBlogPosts'

export default function BlogPageClient({ locale }: { locale: string }) {
  const [selectedCategory, setSelectedCategory] = useState<string>('All')

  // Combine all blog posts
  const allBlogPosts = { ...blogPostsData, ...criticalBlogPosts }

  // Convert to array for display
  const blogPosts = Object.entries(allBlogPosts).map(([key, post]: [string, any]) => {
    // Generate excerpt from content if not provided
    const generateExcerpt = (content: string) => {
      const plainText = content.replace(/[#*`\[\]]/g, '').trim()
      return plainText.substring(0, 150) + (plainText.length > 150 ? '...' : '')
    }

    const excerpt = post.excerpt?.[locale as 'de' | 'en'] ||
                   post.excerpt?.en ||
                   (post.content?.[locale as 'de' | 'en'] ? generateExcerpt(post.content[locale as 'de' | 'en']) :
                    post.content?.en ? generateExcerpt(post.content.en) : '')

    return {
      ...post,
      slug: post.slug || key,
      title: post.title?.[locale as 'de' | 'en'] || post.title?.en || '',
      excerpt,
      readTime: post.readTime?.[locale as 'de' | 'en'] || post.readTime?.en || '5 min read'
    }
  }).sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())

  const categories = [
    'AI',
    'AI Agents',
    'MCP',
    'Development',
    'AI Infrastructure',
    'Security',
  ]

  // Filter blog posts based on selected category
  const filteredPosts = selectedCategory === 'All'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory)

  return (
    <div className="min-h-screen bg-white dark:bg-secondary-900">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-primary-900/10" />
        <div className="container relative">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-6">
            {locale === 'de' ? 'TOBG Blog' : 'TOBG Blog'}
          </h1>
          <p className="text-xl text-center text-secondary-600 dark:text-secondary-400 max-w-3xl mx-auto">
            {locale === 'de'
              ? 'Aktuelle Einblicke in KI-Entwicklungen, AI Agents, MCP-Protokolle und KI-Infrastruktur'
              : 'Current insights into AI developments, AI agents, MCP protocols and AI infrastructure'}
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 border-b border-secondary-200 dark:border-secondary-700">
        <div className="container">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setSelectedCategory('All')}
              className={`px-4 py-2 rounded-full transition-colors ${
                selectedCategory === 'All'
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-secondary-100 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-700'
              }`}
            >
              {locale === 'de' ? 'Alle' : 'All'}
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full transition-colors ${
                  selectedCategory === category
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-secondary-100 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="container">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-secondary-600 dark:text-secondary-400">
                {locale === 'de'
                  ? `Keine Artikel in der Kategorie "${selectedCategory}" gefunden.`
                  : `No articles found in category "${selectedCategory}".`}
              </p>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article key={post.id} className="bg-white dark:bg-secondary-800 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="h-48 bg-gradient-to-br from-primary-500/20 to-primary-900/20 flex items-center justify-center">
                  <span className="text-4xl font-bold text-primary-600/30">{post.category}</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full text-sm">
                      {post.category}
                    </span>
                    <span className="text-sm text-secondary-500 dark:text-secondary-400">
                      {post.readTime}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold mb-3 hover:text-primary-600 transition-colors">
                    <Link href={`/${locale}/blog/${post.slug}`}>
                      {post.title}
                    </Link>
                  </h2>
                  <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-secondary-500 dark:text-secondary-400">
                      {new Date(post.date).toLocaleDateString(locale === 'de' ? 'de-DE' : 'en-US')}
                    </span>
                    <Link
                      href={`/${locale}/blog/${post.slug}`}
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      {locale === 'de' ? 'Weiterlesen →' : 'Read more →'}
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
          )}

          {/* Featured Highlights Section */}
          <div className="mt-16 p-8 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl">
            <h2 className="text-3xl font-bold mb-6 text-center">
              {locale === 'de' ? 'Highlights April 2026' : 'Highlights April 2026'}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-secondary-800 p-6 rounded-lg">
                <h3 className="font-bold text-lg mb-2">Claude Opus 4.6</h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  {locale === 'de'
                    ? 'Neuestes Modell von Anthropic — leistungsstark und datenschutzkonform'
                    : 'Latest model from Anthropic — powerful and privacy-respecting'}
                </p>
              </div>
              <div className="bg-white dark:bg-secondary-800 p-6 rounded-lg">
                <h3 className="font-bold text-lg mb-2">MCP Protocol</h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  {locale === 'de'
                    ? 'Model Context Protocol revolutioniert die KI-Agenten-Architektur'
                    : 'Model Context Protocol revolutionizing AI agent architecture'}
                </p>
              </div>
              <div className="bg-white dark:bg-secondary-800 p-6 rounded-lg">
                <h3 className="font-bold text-lg mb-2">AIPuna Live</h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  {locale === 'de'
                    ? 'TOBG-Flaggschiff-App jetzt im App Store & Google Play verfügbar'
                    : 'TOBG flagship app now live on App Store & Google Play'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-secondary-50 dark:bg-secondary-800">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">
              {locale === 'de' ? 'KI-Updates direkt ins Postfach' : 'AI Updates Straight to Your Inbox'}
            </h2>
            <p className="text-lg text-secondary-600 dark:text-secondary-400 mb-8">
              {locale === 'de'
                ? 'Bleiben Sie auf dem Laufenden über die neuesten Entwicklungen in KI und Automatisierung.'
                : 'Stay up to date with the latest developments in AI and automation.'}
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder={locale === 'de' ? 'Ihre E-Mail-Adresse' : 'Your email address'}
                className="flex-1 px-4 py-3 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-secondary-900 dark:text-white"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                {locale === 'de' ? 'Abonnieren' : 'Subscribe'}
              </button>
            </form>
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-4">
              {locale === 'de'
                ? 'Wöchentliche Updates. Jederzeit abbestellbar.'
                : 'Weekly updates. Unsubscribe anytime.'}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">
              {locale === 'de' ? 'Bereit für Ihre KI-Transformation?' : 'Ready for Your AI Transformation?'}
            </h2>
            <p className="text-xl mb-8 opacity-90">
              {locale === 'de'
                ? 'Nutzen Sie die Kraft von Claude 4, GPT-5 und modernsten KI-Agenten für Ihr Unternehmen.'
                : 'Harness the power of Claude 4, GPT-5, and cutting-edge AI agents for your business.'}
            </p>
            <Link
              href={`/${locale}/contact`}
              className="inline-block px-8 py-3 bg-white text-primary-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold"
            >
              {locale === 'de' ? 'Kontakt aufnehmen' : "Let's Talk"}
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
